import { useEffect } from 'react'
import type { ChatUser } from '../types'
import * as chatStore from '../stores'

type UserEventClient = {
  on: (eventType: 'user.updated', handler: (event: any) => void) => {
    unsubscribe?: () => void
  }
}

type UseSyncUserUpdatesOptions = {
  enabled?: boolean
}

export function getNextCurrentUser(
  currentUser: ChatUser | null,
  eventUser: Partial<ChatUser> & { id: string },
) {
  if (!currentUser || currentUser.id !== eventUser.id) {
    return currentUser
  }

  const nextUser = {
    ...currentUser,
    ...eventUser,
  } as ChatUser & { image?: string | null }

  if (!Object.prototype.hasOwnProperty.call(eventUser, 'image')) {
    nextUser.image = null
  }

  return nextUser as ChatUser
}

/**
 * Keeps chat user state in sync with Stream `user.updated` events.
 *
 * One subscription updates two pieces of state:
 * 1. The current signed-in user snapshot, so local profile data stays fresh.
 * 2. The per-user image override store used by DM avatar surfaces, so avatar-only
 *    rerenders can stay scoped to the UI that actually depends on them.
 *
 * Group chats can continue relying on `channel.data.image`, but 1:1 chat surfaces need
 * a user-level image source that updates in real time when the remote user changes or
 * removes their avatar.
 */
export function useSyncUserUpdates(
  client: UserEventClient | null | undefined,
  options?: UseSyncUserUpdatesOptions,
) {
  const { enabled = true } = options || {}

  useEffect(() => {
    if (!client || !enabled) return

    const subscription = client.on('user.updated', event => {
      const eventUser = event?.user
      if (!eventUser?.id) return

      chatStore.setUpdatedUserImage(eventUser.id, eventUser.image ?? null)

      const currentUser = chatStore.getUser()
      if (currentUser?.id !== eventUser.id) return

      const nextUser = getNextCurrentUser(currentUser, eventUser)

      if (nextUser !== currentUser) {
        chatStore.setUser(nextUser)
      }
    })

    return () => {
      subscription?.unsubscribe?.()
    }
  }, [client, enabled])
}
